import { yupResolver } from '@hookform/resolvers/yup'
import { filter, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { ThemeImages, ThemeImagesPayload } from '../../../common/models'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getThemeImages,
  updateThemeImages,
} from '../../../core/services/festival-settings'
import { ImageConfig, uploadImage } from '../../../core/services/upload-image'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Header, PageTitle } from '@/components'
import { Button, Card, Input } from '@/components/ui'
import { AlertRatio } from '@/components/AlertRatio'

interface IValues {
  content: ThemeImagesPayload[]
}

// Fields that must match an exact pixel size (keyed by ThemeImages.imageKey)
const EXACT_DIMENSIONS: Record<string, { width: number; height: number }> = {
  RADIO_DARK: { width: 133, height: 65 },
  RADIO_LIGHT: { width: 133, height: 65 },
}

const getImageDimensions = (file: File) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read image'))
    }
    img.src = url
  })

export const ThemeImagesPage: React.FC = () => {
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [themeImages, setThemeImages] = useState<ThemeImages[]>([])
  const [reset, setReset] = useState<ThemeImages[]>([])
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getThemeImages()
        setThemeImages(response.data)
        map(response.data, (d, index) => {
          setValue(`content.${index}.id`, d.id)
          setValue(`content.${index}.imageKey`, d.imageKey)
          setValue(`content.${index}.photoKey`, d.imageKey)
        })
        setReset(response.data)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    content: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().required(),
          photoKey: yup.string().required().notOneOf(['']),
          imageKey: yup.string().required(),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<IValues>({
    mode: 'onChange',
    resolver: yupResolver<IValues>(schema),
  })

  const submit = async (values: IValues) => {
    try {
      const { status } = await updateThemeImages(
        filter(
          values.content,
          (content) =>
            content !== null &&
            content !== undefined &&
            content.photoKey !== content.imageKey,
        ),
      )
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  return (
    <>
      <Header pageTitle="Settings" pageSubtitle="Theme Images" />
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120 max-h-[calc(100vh-118px)] overflow-y-auto space-y-4">
        <form
          id="theme-images-form"
          onSubmit={handleSubmit(submit)}
          className="flex flex-col gap-3"
        >
          <h3 className="text-title-xs text-monochrome-1 mb-6">Theme Images</h3>
          {map(themeImages, (image, index) => {
            return (
              <>
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      accept="image/*"
                      disabled={!hasEditPermission}
                      isInvalid={
                        !!errors?.content?.[index]?.photoKey?.message ||
                        invalidPhoto ||
                        isErrorRatio
                      }
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          setInvalidPhoto(false)
                          setIsErrorRatio(false)
                          if (file.size > 1_000_000) {
                            setInvalidPhoto(true)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const required =
                            EXACT_DIMENSIONS[themeImages[index].imageKey]
                          if (required) {
                            const { width, height } = await getImageDimensions(
                              file,
                            )
                            if (
                              width !== required.width ||
                              height !== required.height
                            ) {
                              setInvalidPhoto(true)
                              setIsError(true)
                              setShowMessageBox(true)
                              setMessage(
                                `The image must be exactly ${required.width}x${required.height} px`,
                              )
                              return
                            }
                          }
                          const photo = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            `SETTINGS_THEME_${themeImages[index].imageKey}` as ImageConfig,
                            false,
                          )
                          if (photo) {
                            setValue(
                              `content.${index}.id`,
                              themeImages[index].id,
                              { shouldDirty: true },
                            )
                            setValue(
                              `content.${index}.imageKey`,
                              themeImages[index].imageKey,
                              { shouldDirty: true },
                            )
                            setValue(
                              `content.${index}.photoKey`,
                              photo.photoKey,
                              {
                                shouldDirty: true,
                              },
                            )

                            const items = [...themeImages]
                            const item = { ...items[index] }
                            item.imageLink = photo.photoUrl
                            items[index] = item
                            setThemeImages(items)
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  disabled={!hasEditPermission}
                  imageName={themeImages[index].imageKey}
                  isUploading={isUploading}
                  url={themeImages[index].imageLink}
                  onRemove={() => {
                    setValue(`content.${index}.photoKey`, '')

                    const items = [...themeImages]
                    const item = { ...items[index] }
                    item.imageLink = ''
                    items[index] = item
                    setThemeImages(items)
                  }}
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                  label={
                    EXACT_DIMENSIONS[themeImages[index].imageKey]
                      ? `${themeImages[index].imageLabel} (${
                          EXACT_DIMENSIONS[themeImages[index].imageKey].width
                        }x${
                          EXACT_DIMENSIONS[themeImages[index].imageKey].height
                        } px)`
                      : themeImages[index].imageLabel
                  }
                  previewLabel={themeImages[index].imageLabel}
                />
                {isErrorRatio && <AlertRatio />}
              </>
            )
          })}
        </form>

        <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
          <Button
            onClick={() => {
              setThemeImages(reset)
            }}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="theme-images-form"
            type="submit"
            disabled={isSubmitting || isUploading || !hasEditPermission}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
